import React, { useState, useEffect, useMemo } from 'react';

const SimpleNavbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('inicio');
  const [scrollProgress, setScrollProgress] = useState(0);

  const navItems = useMemo(() => [
    { href: '#inicio', label: 'Inicio', id: 'inicio' },
    { href: '#servicios', label: 'Servicios', id: 'servicios' },
    { href: '#instalaciones', label: 'Instalaciones', id: 'instalaciones' },
    { href: '#nosotros', label: 'Nosotros', id: 'nosotros' },
    { href: '#contacto', label: 'Contacto', id: 'contacto' },
  ], []);

  // Track active section and scroll progress
  useEffect(() => {
    const handleScroll = () => {
      const sections = navItems.map(item => item.id);
      const scrollPosition = window.scrollY + 100; // Offset for navbar height

      // Calculate scroll progress
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / documentHeight) * 100;
      setScrollProgress(Math.min(progress, 100));

      // Determine active section
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position

    return () => window.removeEventListener('scroll', handleScroll);
  }, [navItems]);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    const targetElement = document.querySelector(href);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <nav className="bg-white/95 backdrop-blur-sm shadow-lg fixed w-full z-50 transition-all duration-300">
      {/* Progress Bar */}
      <div className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-purple-500 to-purple-700 transition-all duration-300 ease-out"
           style={{ width: `${scrollProgress}%` }}></div>
      
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div 
            className="flex items-center group cursor-pointer"
            onClick={(e) => handleNavClick(e, '#inicio')}
          >
            <div className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center group-hover:bg-purple-600 transition-all duration-300 group-hover:scale-110">
              <i className="fas fa-paw text-white text-2xl group-hover:rotate-12 transition-transform duration-300"></i>
            </div>
            <span className="ml-3 text-3xl font-bold text-purple-700 group-hover:text-purple-800 transition-colors duration-300">CEV24</span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex space-x-8">
            {navItems.map((item, index) => (
              <a
                key={index}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className={`font-medium relative transition-all duration-300 py-2 px-3 rounded-lg group ${
                  activeSection === item.id
                    ? 'text-purple-600 bg-purple-50'
                    : 'text-gray-700 hover:text-purple-500 hover:bg-purple-50'
                }`}
              >
                {item.label}
                <span className={`absolute bottom-1 left-1/2 transform -translate-x-1/2 h-0.5 bg-purple-500 transition-all duration-300 ${
                  activeSection === item.id ? 'w-4' : 'w-0 group-hover:w-4'
                }`}></span>
              </a>
            ))}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button 
              className="text-gray-700 focus:outline-none hover:text-purple-500 transition-colors duration-300 p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <i className={`fas ${isMobileMenuOpen ? 'fa-times' : 'fa-bars'} text-2xl transition-all duration-300 ${isMobileMenuOpen ? 'rotate-90' : ''}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className={`md:hidden bg-white/95 backdrop-blur-sm shadow-lg transition-all duration-300 overflow-hidden ${isMobileMenuOpen ? 'max-h-96 py-4' : 'max-h-0'}`}>
        {navItems.map((item, index) => (
          <a
            key={index}
            href={item.href}
            onClick={(e) => handleNavClick(e, item.href)}
            className={`block px-4 py-3 font-medium transition-all duration-300 transform hover:translate-x-2 ${
              activeSection === item.id
                ? 'text-purple-600 bg-purple-50 border-r-2 border-purple-500'
                : 'text-gray-700 hover:bg-purple-50 hover:text-purple-600'
            }`}
            style={{ 
              animationDelay: `${index * 0.1}s`,
              opacity: isMobileMenuOpen ? 1 : 0,
              transform: isMobileMenuOpen ? 'translateY(0)' : 'translateY(-10px)'
            }}
          >
            {item.label}
          </a>
        ))}
      </div>
    </nav>
  );
};

export default SimpleNavbar;